import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Button } from 'reactstrap';
import { Person, ReduxState } from 'types';
import { ADMIN_ONLY_PERSON_FIELDS } from 'utils/models/Person';
import FilterablePeopleTable, {
  FilterType,
} from 'views/Widgets/People/FilterablePeopleTable';
import {
  FORMAT_AVATAR_WITH_TITLE,
  getIconForFieldName,
  usePeopleInOrganizationForFiltering,
} from 'views/Widgets/People/Filters/common';
import { getDescriptionForFieldName } from 'views/Widgets/People/Table/commons';
import Scrollable from 'views/Widgets/Scrollable';

interface Props {
  excludePersonIds: number[];
  onAddPeople: (people: Person[]) => void;
  onClose: () => void;
}

const EMPTY_SELECTED_SET = new Set<string>();

const getUniqueRowId: ((...args: any[]) => any) | null | undefined = (row) =>
  String(row.id);
const isRowSelectable: ((...args: any[]) => any) | null | undefined = (row) =>
  Boolean(row?.id);

const AddReviewersFromFilter: FC<Props> = ({
  excludePersonIds,
  onAddPeople,
  onClose,
}) => {
  const { formatMessage } = useIntl();

  const currentOrganization = useSelector(
    (state: ReduxState) => state.currentOrganization
  );
  const currentProxyPerson = useSelector(
    (state: ReduxState) => state.currentProxyPerson
  );

  const [allPeople] = usePeopleInOrganizationForFiltering({
    currentOrganization,
    currentProxyPerson,
  });

  const [selectedIds, setSelectedIds] =
    useState<Set<string>>(EMPTY_SELECTED_SET);

  // Reset selection when the people list changes (e.g. org switch)
  useEffect(() => {
    setSelectedIds(EMPTY_SELECTED_SET);
  }, [allPeople]);

  const rows = useMemo(() => {
    if (!allPeople) return [];
    const excludeSet = new Set(excludePersonIds.map(String));
    return allPeople.filter((p) => !excludeSet.has(String(p.id)));
  }, [allPeople, excludePersonIds]);

  const columns = useMemo(() => {
    const nameColumn = {
      field: 'person',
      name: formatMessage({
        id: 'app.views.widgets.dashboards.calibration_dashboard.components.add_reviewers_from_filter.column.name',
        defaultMessage: 'Name',
      }),
      format: FORMAT_AVATAR_WITH_TITLE,
      sortable: true,
    };

    // All person fields that can be filtered on. `manager` is a Person object, the rest
    // are scalar/label fields. We skip any field the filter UI has no human-readable label for
    // (otherwise it would render the raw underscored field name, e.g. `original_hire_date`).
    const candidateFilterColumns = [
      {
        field: 'manager',
        isPerson: true,
        getFilterDisplayValue: (p: Person) => p.full_name,
      },
      ...ADMIN_ONLY_PERSON_FIELDS.filter(
        (field) => getDescriptionForFieldName(field, formatMessage) !== field
      ).map((field) => ({ field })),
    ];

    // Only surface a filter for columns that have at least one row with a
    // value. They are hidden from the table and used for filtering only.
    const filterColumns = candidateFilterColumns
      .filter((column) =>
        rows.some(
          (row: Person) =>
            (row as unknown as Record<string, unknown>)[column.field]
        )
      )
      .map((column) => ({
        ...column,
        filterOnly: true,
        filterIcon: getIconForFieldName(column.field),
      }));

    return [nameColumn, ...filterColumns];
  }, [formatMessage, rows]);

  const toggleSelectedRowField = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleAddSelected = useCallback(() => {
    const selectedPeople = rows.filter((p) =>
      selectedIds.has(String(p.id))
    ) as Person[];
    onAddPeople(selectedPeople);
  }, [rows, selectedIds, onAddPeople]);

  return (
    <div className="mt-2 border rounded p-3">
      <Scrollable>
        <FilterablePeopleTable
          rows={rows ?? []}
          columns={columns}
          filterType={FilterType.ADVANCED}
          selectedRowToggleFieldsSet={selectedIds}
          setSelectedRowToggleFieldsSet={setSelectedIds}
          toggleSelectedRowField={toggleSelectedRowField}
          getUniqueRowId={getUniqueRowId}
          isRowSelectable={isRowSelectable}
          hideExportButton={true}
          hideColumnSelector={true}
          showToggleFilters={true}
          defaultShowFilters={true}
          hideHeader={true}
          bookmarkUrl={false}
        />
      </Scrollable>
      <div className="d-flex align-items-center gap-2 mt-2">
        <Button
          color="primary"
          size="sm"
          disabled={selectedIds.size === 0}
          onClick={handleAddSelected}
        >
          <FormattedMessage
            id="app.views.widgets.dashboards.calibration_dashboard.components.add_reviewers_from_filter.add_selected"
            defaultMessage="Add selected and close"
          />
        </Button>
        <Button color="link" size="sm" className="p-0" onClick={onClose}>
          <FormattedMessage
            id="app.views.widgets.dashboards.calibration_dashboard.components.add_reviewers_from_filter.cancel"
            defaultMessage="Cancel"
          />
        </Button>
      </div>
    </div>
  );
};

export default AddReviewersFromFilter;
